import React, { useMemo } from "react"
import { marked } from "marked"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { usePageTitle } from "../hooks/usePageTitle"

const PROSE =
  "w-full max-w-[900px] text-[16px] leading-[1.7] text-text " +
  "[&_h1]:m-0 [&_h1]:mb-2 [&_h1]:text-[32px] md:[&_h1]:text-[44px] [&_h1]:font-bold [&_h1]:leading-tight " +
  "[&_p]:my-4 [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-[24px] [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-[19px] [&_h3]:font-semibold " +
  "[&_ol]:list-decimal [&_ol]:pl-7 [&_ol]:my-3 [&_ul]:list-disc [&_ul]:pl-7 [&_ul]:my-3 [&_li]:my-1.5 [&_li]:pl-1 " +
  "[&_a]:text-primary [&_a]:underline [&_a]:break-words [&_strong]:font-bold " +
  "[&_hr]:my-8 [&_hr]:border-bordered " +
  "[&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 " +
  "[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-bordered [&_th]:p-2 [&_th]:text-left " +
  "[&_td]:border [&_td]:border-bordered [&_td]:p-2 [&_td]:align-top " +
  "[&_mark]:rounded [&_mark]:bg-[#fff3b0] [&_mark]:px-1 [&_mark]:text-black"

// [[...]] in the texts marks what is still to be filled in or confirmed:
// shown highlighted until it is replaced
export const legalHtml = (markdown) => marked.parse(markdown).replace(/\[\[(.+?)\]\]/g, "<mark>$1</mark>")
const html = legalHtml

/**
 * One marked part of a legal text: from its anchor (`<a id="…"></a>`) to its
 * end mark (`<!-- /… -->`) — e.g. the Terms' standard return policy, shown on
 * its own in the vendor portal. The whole text when the part is not found.
 */
export function legalSection(markdown, id) {
  const text = String(markdown || "")
  const start = text.indexOf(`<a id="${id}"></a>`)
  const end = text.indexOf(`<!-- /${id} -->`)
  if (start < 0 || end < start) return text
  return text.slice(start, end)
}

/**
 * A legal text of the site (Terms of Use, Returns, Refunds), written in
 * Markdown under Storefront/legal/<lang>/, in the site's language: Bulgarian
 * on the Bulgarian site, English everywhere else.
 */
export default function LegalPage({ title, texts }) {
  const language = useSiteLanguage()
  usePageTitle(title)
  const content = useMemo(() => html(texts[language] || texts.en), [language, texts])
  return (
    <div className="w-full flex flex-col items-center px-4 sm:px-8 pt-10 pb-24 md:pt-16">
      <div className={PROSE} dangerouslySetInnerHTML={{ __html: content }} />
    </div>
  )
}
